import HostHelpSection from "@/components/HostHelpSection";
import { BodyContainer } from "@/components/Layout";
import { InlineSpinner } from "@/components/Spinner";
import { useQueryOptionsFactory } from "@/queries";
import { useActiveClient } from "@/routes/$clientId.tsx";
import { Alert } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import ConfigurationCard from "./-components/ConfigurationCard.tsx";
import LogEntryList from "./-components/LogEntryList.tsx";

const Root = (): ReactNode => {
    const { id: clientId } = useActiveClient();
    const params = Route.useParams();
    const qof = useQueryOptionsFactory();
    const { data: configuration } = useSuspenseQuery(
        qof.privateBackup.getConfiguration(clientId, params.hostId),
    );
    const { data: logEntries } = useSuspenseQuery(
        qof.privateBackup.listLogEntries(clientId, params.hostId),
    );

    return (
        <div>
            <ConfigurationCard configuration={configuration} />

            {logEntries.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    No backups have been performed yet.
                </Alert>
            ) : (
                <LogEntryList logEntries={logEntries} sx={{ mt: 2 }} />
            )}

            <HostHelpSection
                supportResources={[
                    { label: "Using OptiFlex Private Backup", type: "private_backup" },
                ]}
            />
        </div>
    );
};

export const Route = createFileRoute("/$clientId/hosts/$hostId/private-backup/")({
    component: Root,
    pendingComponent: () => (
        <BodyContainer>
            <InlineSpinner />
        </BodyContainer>
    ),
    loader: async ({ context, params }) => {
        await Promise.all([
            context.queryClient.ensureQueryData(
                context.qof.privateBackup.getConfiguration(params.clientId, params.hostId),
            ),
            context.queryClient.ensureQueryData(
                context.qof.privateBackup.listLogEntries(params.clientId, params.hostId),
            ),
        ]);
    },
});
